import { useEffect, useRef } from "react";
import { useDispatch, useStore } from "react-redux";
import type { AppDispatch } from "@/features/redux/store";
import { fetcher } from "@/api/api";
import { useGlobalState } from "@/store/commonStore";
import { reset } from "./modalOrchestratorSlice";
import { clearAllSessionOnceKeys } from "./sessionOnce";
import {
  fetchPopupActivityList,
  getPopupActivityRequestScope,
} from "./popupActivityList";
import {
  enqueuePostAuthPopupTasks,
  type PopupResult,
} from "./postAuthPopupTasks";

// ---- 注册标记 ----

// 注册是当前页面内的业务事件，刷新不继承上一轮未消费的注册标记。
let justRegistered = false;

export const markJustRegistered = (): void => {
  justRegistered = true;
};

const consumeRegisterFlag = (): boolean => {
  const result = justRegistered;
  justRegistered = false;
  return result;
};

const settle = <T>(request: Promise<T>): Promise<PopupResult<T>> =>
  request.then(
    (value) => ({ ok: true as const, value }),
    () => ({ ok: false as const })
  );

const fetchBatch = (requestScope: object) =>
  Promise.all([
    settle(
      fetcher<any[]>({ path: "api/index/loginPop", type: "get" }, { arg: {} })
    ),
    settle(
      fetcher<any>(
        { path: "api/index/loginSpecialActivity", type: "get" },
        { arg: null }
      )
    ),
    settle(fetchPopupActivityList(requestScope)),
    settle(
      fetcher<any>({ path: "api/member/info", type: "post" }, { arg: {} })
    ),
  ] as const);

type Batch = {
  token: string;
  requestScope: object;
  isRegister: boolean;
  request?: ReturnType<typeof fetchBatch>;
  settled: boolean;
  retryNeeded: boolean;
  automaticRetries: number;
};
type Runtime = {
  batch?: Batch;
  userId?: string;
  applied: Set<string>;
};

// store 属于当前文档；组件重新挂载不重建本轮，整页刷新/新标签页会重建。
const runtimes = new WeakMap<object, Runtime>();

export const usePostAuthPopups = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore();
  const { isLogin, token } = useGlobalState();
  const authRef = useRef({ isLogin, token });
  authRef.current = { isLogin, token };

  useEffect(() => {
    const requestScope = getPopupActivityRequestScope(
      store,
      isLogin && token ? token : null
    );
    let runtime = runtimes.get(store);
    if (!runtime) {
      runtime = { applied: new Set() };
      runtimes.set(store, runtime);
    }

    if (!isLogin || !token) {
      if (runtime.batch) {
        runtime.batch = undefined;
        runtime.userId = undefined;
        runtime.applied.clear();
        clearAllSessionOnceKeys(store);
        dispatch(reset());
      }
      return;
    }

    if (runtime.batch?.token !== token) {
      runtime.batch = {
        token,
        requestScope,
        isRegister: consumeRegisterFlag(),
        settled: false,
        retryNeeded: false,
        automaticRetries: 0,
      };
    }
    const batch = runtime.batch;
    let alive = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const isCurrent = () =>
      alive &&
      runtime.batch === batch &&
      authRef.current.isLogin &&
      authRef.current.token === token;

    const run = async () => {
      // StrictMode 和重挂载复用请求及注册事件；仅有效观察者可以提交结果。
      if (!batch.request) {
        batch.settled = false;
        batch.request = fetchBatch(batch.requestScope);
      }
      const [notices, special, activities, profile] = await batch.request;
      batch.settled = true;
      if (!isCurrent()) return;

      const userInfo = profile.ok ? profile.value : null;
      const hasIdentity = userInfo?.userId != null;
      if (hasIdentity) {
        const userId = String(userInfo.userId);
        if (runtime.userId !== undefined && runtime.userId !== userId) {
          dispatch(reset());
          clearAllSessionOnceKeys(store);
          runtime.applied.clear();
        }
        runtime.userId = userId;

        enqueuePostAuthPopupTasks(
          dispatch,
          runtime.applied,
          batch.isRegister,
          userInfo,
          notices,
          special,
          activities
        );
      }

      batch.retryNeeded =
        !hasIdentity || !notices.ok || !special.ok || !activities.ok;
      // 短暂失败不消耗整轮。一次自动重试，此后仅在网络恢复/重新可见时重试。
      if (batch.retryNeeded && batch.automaticRetries < 1) {
        retryTimer = setTimeout(() => {
          batch.automaticRetries += 1;
          retry();
        }, 3000);
      }
    };

    function retry() {
      if (
        !isCurrent() ||
        !batch.retryNeeded ||
        !batch.settled ||
        document.visibilityState === "hidden"
      )
        return;
      batch.request = undefined;
      batch.retryNeeded = false;
      void run();
    }
    const onVisible = () => {
      if (document.visibilityState === "visible") retry();
    };
    void run();
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      alive = false;
      clearTimeout(retryTimer);
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [dispatch, store, isLogin, token]);
};
